import type { LayerDef, Legend } from "../types";

/**
 * How the transit network is drawn: one dot per stop, or the lines themselves.
 *
 * Both are built into the style at load and only one is ever visible, so
 * switching is a visibility flip like every other toggle in the app.
 */
export type TransitView = "stops" | "lines";

/** Mode colours, close to the network's own signage so they read instantly. */
export const MODE_COLORS: Record<string, string> = {
  metro: "#0055c8",
  rer: "#d62828",
  rail: "#00996b",
  tram: "#8e44ad",
  bus: "#c8811e",
  ferry: "#0d7d8f",
  cable: "#6b7280",
  funicular: "#6b7280",
};

export const MODE_LABELS: Record<string, string> = {
  metro: "Métro",
  rer: "RER",
  rail: "Transilien",
  tram: "Tram",
  bus: "Bus",
};

/**
 * The official line palette, for the legend only, grouped the way the network
 * is: a passenger picks "the trams", not "the route_type 0 routes".
 *
 * Every line's colour also travels on the tile as a `color` attribute, which is
 * what the map paints with — that is the authoritative copy, and it covers
 * lines this table does not (TER, Orlyval, CDG VAL). This table exists because
 * the legend has to name lines that may be nowhere in the current viewport.
 * Values come from IDFM's charte graphique, p.106; keep it in step with
 * `LINE_COLORS` in `etl/mete_etl/transit.py`.
 */
const LINE_INDEX: { mode: keyof typeof MODE_LABELS; lines: [line: string, color: string, text: string][] }[] = [
  {
    mode: "metro",
    lines: [
      ["1", "#ffbe00", "#000000"], ["2", "#0055c8", "#ffffff"],
      ["3", "#6e6e00", "#ffffff"], ["3bis", "#82c8e6", "#000000"],
      ["4", "#a0006e", "#ffffff"], ["5", "#ff5a00", "#000000"],
      ["6", "#82dc73", "#000000"], ["7", "#ff82b4", "#000000"],
      ["7bis", "#82dc73", "#000000"], ["8", "#d282be", "#000000"],
      ["9", "#d2d200", "#000000"], ["10", "#dc9600", "#000000"],
      ["11", "#6e491e", "#ffffff"], ["12", "#00643c", "#ffffff"],
      ["13", "#82c8e6", "#000000"], ["14", "#640082", "#ffffff"],
      // Grand Paris Express — under construction, drawn dashed. They sit with
      // the métro because that is what they will be.
      ["15", "#a50034", "#ffffff"], ["16", "#ff82b4", "#000000"],
      ["17", "#d2d200", "#000000"], ["18", "#00a092", "#ffffff"],
    ],
  },
  {
    mode: "rer",
    lines: [
      ["A", "#ff1400", "#ffffff"], ["B", "#3c91dc", "#ffffff"],
      ["C", "#ffbe00", "#000000"], ["D", "#00643c", "#ffffff"],
      ["E", "#a0006e", "#ffffff"],
    ],
  },
  {
    mode: "rail",
    lines: [
      ["H", "#6e491e", "#ffffff"], ["J", "#d2d200", "#000000"],
      ["K", "#6e6e00", "#ffffff"], ["L", "#d282be", "#000000"],
      ["N", "#00a092", "#ffffff"], ["P", "#ff5a00", "#000000"],
      ["R", "#ff82b4", "#000000"], ["U", "#a50034", "#ffffff"],
      ["V", "#6e6e00", "#ffffff"],
    ],
  },
  {
    mode: "tram",
    lines: [
      ["T1", "#0055c8", "#ffffff"], ["T2", "#a0006e", "#ffffff"],
      ["T3a", "#ff5a00", "#000000"], ["T3b", "#00643c", "#ffffff"],
      ["T4", "#dc9600", "#000000"], ["T5", "#640082", "#ffffff"],
      ["T6", "#ff1400", "#ffffff"], ["T7", "#6e491e", "#ffffff"],
      ["T8", "#6e6e00", "#ffffff"], ["T9", "#3c91dc", "#ffffff"],
      ["T10", "#6e6e00", "#ffffff"], ["T11", "#ff5a00", "#000000"],
      ["T12", "#a50034", "#ffffff"], ["T13", "#6e491e", "#ffffff"],
      ["T14", "#00a092", "#ffffff"],
    ],
  },
];

const colorExpr = [
  "match",
  ["get", "mode"],
  ...Object.entries(MODE_COLORS).flatMap(([k, v]) => [k, v]),
  "#6b7280",
];

/** Métro and RER carry the network; they are drawn heavier than the rest. */
const isTrunk = ["match", ["get", "mode"], ["metro", "rer"], true, false];

/** Line width, and the white casing that separates it from what it crosses. */
const lineWidth = [
  "interpolate", ["exponential", 1.3], ["zoom"],
  8, ["case", isTrunk, 1.3, 0.9],
  11, ["case", isTrunk, 2.6, 1.8],
  14, ["case", isTrunk, 5.5, 3.8],
];
const casingWidth = [
  "interpolate", ["exponential", 1.3], ["zoom"],
  8, ["case", isTrunk, 2.5, 2],
  11, ["case", isTrunk, 4.4, 3.4],
  14, ["case", isTrunk, 8, 6],
];

/**
 * Opacity for a line the user has focused away from. Low enough to recede,
 * high enough that the rest of the network still reads as context rather than
 * disappearing — the point of picking a line is usually to see where it sits.
 */
const DIMMED_OPACITY = 0.12;

/**
 * Line layers that dim, and the `line-opacity` each sits at when nothing is
 * focused. Dimming is a paint-property change on geometry already in memory,
 * so picking a line out of the legend costs a repaint and no network traffic.
 */
export const TRANSIT_DIMMABLE: { id: string; base: number }[] = [
  { id: "transit-lines-casing", base: 0.85 },
  { id: "transit-lines", base: 1 },
  { id: "transit-lines-construction", base: 1 },
];

export const TRANSIT_LABEL_LAYER = "transit-lines-label";

/** Full opacity for the focused lines, dimmed for the rest. */
export function lineOpacity(highlighted: string[], base: number) {
  if (highlighted.length === 0) return base;
  return [
    "case",
    ["in", ["get", "line"], ["literal", highlighted]],
    base,
    DIMMED_OPACITY,
  ];
}

/**
 * Labels are filtered rather than dimmed: a faded index still takes part in
 * collision detection, and would push out the label of the line you asked for.
 */
export function lineLabelFilter(highlighted: string[]) {
  return highlighted.length === 0
    ? null
    : ["in", ["get", "line"], ["literal", highlighted]];
}

export const TRANSIT_LEGENDS: Record<TransitView, Legend> = {
  stops: {
    stops: Object.entries(MODE_LABELS).map(([k, label]) => ({
      color: MODE_COLORS[k],
      label,
    })),
  },
  lines: {
    stops: [],
    chips: LINE_INDEX.flatMap(({ mode, lines }) =>
      lines.map(([label, color, textColor]) => ({
        label,
        color,
        textColor,
        group: MODE_LABELS[mode],
      })),
    ),
    note: "Dashed portions are still under construction.",
  },
};

/** Which MapLibre layers belong to which view. Everything else is hidden. */
export const TRANSIT_VIEW_LAYERS: Record<TransitView, string[]> = {
  stops: ["transit-bus", "transit-rail"],
  lines: [
    "transit-lines-casing",
    "transit-lines",
    "transit-lines-construction",
    "transit-lines-label",
  ],
};

export const transitLayer: LayerDef = {
  id: "transit",
  label: "Transit",
  group: "transit",
  description:
    "Métro, RER, Transilien, tram and bus — as stops sized by weekday service, or as the lines themselves.",
  attribution:
    '<a href="https://transport.data.gouv.fr/datasets/horaires-prevus-sur-les-lignes-de-transport-en-commun-dile-de-france-gtfs-datahub">IDFM · GTFS</a> · <a href="https://openstreetmap.org">© OpenStreetMap</a>',
  defaultVisible: false,
  legend: TRANSIT_LEGENDS.stops,
  legendFor: (ctx) => TRANSIT_LEGENDS[ctx.transitView as TransitView],
  sources: {
    // Two archives for the stops, not one: tile thinning is decided by feature
    // density, and 30,000 bus stops would take the métro network down with
    // them at city zoom. Split, the rail network is small enough to keep in
    // full.
    transit_rail: { type: "vector", url: "pmtiles:///tiles/transit_rail.pmtiles" },
    transit_bus: { type: "vector", url: "pmtiles:///tiles/transit_bus.pmtiles" },
    transit_lines: { type: "vector", url: "pmtiles:///tiles/transit_lines.pmtiles" },
  },
  layers: [
    // The casing goes down first so every line's halo sits under every line,
    // rather than a later line's white edge cutting through an earlier one.
    {
      id: "transit-lines-casing",
      type: "line",
      source: "transit_lines",
      "source-layer": "lines",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#ffffff",
        "line-width": casingWidth as never,
        "line-opacity": 0.85,
      },
    },
    {
      id: "transit-lines",
      type: "line",
      source: "transit_lines",
      "source-layer": "lines",
      filter: ["!=", ["get", "status"], "construction"] as never,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        // Painted straight from the tile: the ETL resolves the official colour
        // per line, so a line the palette gains later needs no frontend change.
        "line-color": ["get", "color"] as never,
        "line-width": lineWidth as never,
      },
    },
    // The Grand Paris Express lines get their own layer purely because
    // `line-dasharray` is one of the few paint properties MapLibre cannot
    // drive from a feature attribute — one layer filtered each way is the only
    // way to draw built and unbuilt track differently from the same tileset.
    // The gaps let the white casing through, which is what reads as "dashed".
    {
      id: "transit-lines-construction",
      type: "line",
      source: "transit_lines",
      "source-layer": "lines",
      filter: ["==", ["get", "status"], "construction"] as never,
      layout: { "line-cap": "butt", "line-join": "round" },
      paint: {
        "line-color": ["get", "color"] as never,
        "line-width": lineWidth as never,
        // In line-width units, so the dash keeps its proportions as the line
        // thickens with zoom rather than turning into a dotted hairline.
        "line-dasharray": [1.8, 1.2],
      },
    },
    {
      id: "transit-lines-label",
      type: "symbol",
      source: "transit_lines",
      "source-layer": "lines",
      // Below this the network is dense enough that the indices collide more
      // than they inform.
      minzoom: 10,
      layout: {
        "symbol-placement": "line",
        // Wide enough that a line reads as a line rather than a string of
        // repeated numbers, close enough that one is usually in view.
        "symbol-spacing": 320,
        "text-field": ["get", "line"] as never,
        "text-font": ["Noto Sans Medium"] as never,
        "text-size": ["interpolate", ["linear"], ["zoom"], 10, 10, 14, 12] as never,
        "text-rotation-alignment": "viewport",
        "text-pitch-alignment": "viewport",
        "text-padding": 4,
      },
      paint: {
        // The line's own colour as a halo around its index is what turns the
        // label into the signage-style shield the network uses.
        "text-color": ["get", "text_color"] as never,
        "text-halo-color": ["get", "color"] as never,
        "text-halo-width": 2,
      },
    },
    {
      id: "transit-bus",
      type: "circle",
      source: "transit_bus",
      "source-layer": "stops",
      // 30k bus stops would bury the map at region zoom; they only become
      // useful once you are looking at a specific street.
      minzoom: 14,
      filter: ["==", ["get", "mode"], "bus"] as never,
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 14, 2, 16, 3.5] as never,
        "circle-color": MODE_COLORS.bus,
        "circle-opacity": 0.75,
      },
    },
    {
      id: "transit-rail",
      type: "circle",
      source: "transit_rail",
      "source-layer": "stops",
      minzoom: 9,
      filter: ["!=", ["get", "mode"], "bus"] as never,
      paint: {
        // Busier stations read as bigger dots, so the structural network
        // stands out from a lightly-served halt.
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          9, ["interpolate", ["linear"], ["get", "calls"], 0, 1.5, 400, 3.5],
          13, ["interpolate", ["linear"], ["get", "calls"], 0, 3, 400, 7],
          16, ["interpolate", ["linear"], ["get", "calls"], 0, 5, 400, 11],
        ] as never,
        "circle-color": colorExpr as never,
        "circle-stroke-width": 1.2,
        "circle-stroke-color": "#ffffff",
      },
    },
  ],
};
